import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { CheckmarkSmallIcon, MinusIcon } from '@dynatrace/strato-icons';

import {
  checkboxIconCSS,
  checkboxKnobCSS,
  checkboxLabelCSS,
} from './Checkbox.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useAriaLabelingProps as _useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useSafeForwardProps as _useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FocusEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';

const COMPONENT_NAME = 'Checkbox';

// #region Typings
/**
 * @public
 */
export type CheckboxProps = FormControlProps<
  boolean | 'indeterminate',
  (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void | never
> &
  FocusEvents &
  StylingProps &
  DataTestId &
  WithChildren &
  MaskingProps &
  BehaviorTrackingProps & {
    /**
     * A string representing the value used for the checkbox.
     * When submitted in a form, it is only submitted if checked, with the set value.
     * If undefined, the string 'on' is submitted by default as per https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-value.
     */
    formValue?: string;
  };
// #endregion

/**
 * Checkboxes allow the user to select one or more options from a list of options.
 * @public
 */
export const Checkbox = forwardRef<FormControlRef, CheckboxProps>(
  (props, forwardedRef) => {
    const {
      disabled: propDisabled,
      required: propRequired,
      readOnly: propReadOnly,
      id: propId,
      defaultValue,
      value,
      name,
      formValue,
      onValidityChange,
      onChange,
      children,
      controlState,
      className: consumerClassName,
      style: consumerStyle,
      'aria-disabled': ariaDisabledProp,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: CheckboxProps = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    const ariaLabelingProps = _useAriaLabelingProps(restProps);
    const forwardProps = _useSafeForwardProps(restProps, ariaLabelingProps);

    const { state: messagesContextState, dispatch: messagesContextDispatch } =
      useFormFieldMessagesContext();

    const {
      disabled,
      required,
      id,
      ariaDisabled,
      readOnly: _readOnly,
    } = useSyncedFormControlProps({
      propDisabled,
      propRequired,
      propId,
      propAriaDisabled: ariaDisabledProp,
      propReadOnly,
    });

    // Imperative Handle Construction
    const intl = useIntl();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const _emitValidationState = useValidationStateEmitter(onValidityChange);
    const emitValidationState = useCustomMessageEmitter(
      _emitValidationState,
      () => getI18nValidationState(intl, inputRef.current, 'manual'),
    );
    const {
      onChange: validationOnChange,
      onBlur: validationOnBlur,
      onInvalid: validationOnInvalid,
      ariaProps,
    } = useFormFieldValidation(
      inputRef,
      { required, value: String(value) },
      ariaLabelingProps,
      undefined,
      _emitValidationState,
    );

    useImperativeHandle(forwardedRef, (): FormControlRef => {
      return {
        element: rootRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          if (messagesContextState) {
            inputRef.current?.checkValidity();
            messagesContextDispatch?.({
              type: FormFieldMessagesAction.SETISTOUCHED,
              payload: { isTouched: true },
            });
            emitValidationState(
              getI18nValidationState(intl, inputRef.current, 'manual'),
            );
          }
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          return emitValidationState(
            getI18nValidationState(intl, inputRef.current, 'manual'),
          );
        },
      };
    });

    const [state, setState] = useControllableState({
      defaultValue,
      value,
    });

    const disabledOrAriaDisabled = disabled || ariaDisabled || false;
    const readOnly = !!_readOnly;

    const [hasError, hint] = useFormControlState(controlState);
    // _useInputHint will be replaced with new validation concept. APPDEV-8298
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { inputProps, hintProps } = useInputHint({
      hint,
      hasError,
    });

    const showError = hasError || messagesContextState.showError;

    let focusRingColor: 'neutral' | 'critical' | 'primary' = 'neutral';
    if (showError) {
      focusRingColor = 'critical';
    } else if (!(disabled || readOnly) && state) {
      focusRingColor = 'primary';
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
      if (readOnly) {
        return;
      }

      const newValue = e.target.indeterminate ? true : e.target.checked;
      setState(newValue);
      onChange?.(newValue, e);
      validationOnChange();
    };

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = value === 'indeterminate';
      }
    }, [value]);

    let fieldColorVariant: 'default' | 'accent' | 'emphasized' = 'default';
    if (state === true || state === 'indeterminate') {
      fieldColorVariant = 'accent';
    } else if (showError || disabledOrAriaDisabled) {
      fieldColorVariant = 'emphasized';
    }

    let fieldColorColor: 'neutral' | 'primary' | 'critical' = 'primary';
    if (showError) {
      fieldColorColor = 'critical';
    } else if (state === false) {
      fieldColorColor = 'neutral';
    }

    return (
      <Flex
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        flexDirection="column"
        gap={4}
        className={consumerClassName}
        style={consumerStyle}
        {...mergeProps(forwardProps, behavioralTrackingProps)}
        data-dt-component={COMPONENT_NAME}
      >
        <label
          htmlFor={id}
          className={clsx(
            checkboxLabelCSS({
              required: typeof children === 'string' && required,
              disabled: disabledOrAriaDisabled,
              readOnly,
            }),
            textStyleCSS({ fontStyle: 'text', textStyle: 'base' }),
          )}
        >
          <div
            className={clsx(
              checkboxKnobCSS({
                disabled: disabledOrAriaDisabled,
                hasChildren: !!children,
                hasError: showError,
                checked: state === true || state === 'indeterminate',
                readOnly,
              }),
              fieldColorsCSS({
                variant: fieldColorVariant,
                disabled: disabledOrAriaDisabled,
                color: fieldColorColor,
              }),
              focusRingStatic({
                when: 'focus-within-visible',
                color: focusRingColor,
                disabled,
              }),
            )}
          >
            <VisuallyHidden>
              <input
                id={id}
                ref={inputRef}
                type="checkbox"
                aria-checked={state === 'indeterminate' ? 'mixed' : state}
                aria-required={required}
                aria-disabled={ariaDisabled}
                readOnly={readOnly}
                name={name}
                value={formValue}
                disabled={disabled}
                required={required}
                checked={state === true}
                onChange={handleOnChange}
                onBlur={(event) => {
                  validationOnBlur(event);
                }}
                onInvalid={validationOnInvalid}
                {...(controlState ? inputProps : ariaProps)}
              />
            </VisuallyHidden>
            <div className={checkboxIconCSS({ readOnly, hasError: showError })}>
              <CheckboxIconSvg state={state} />
            </div>
          </div>
          {children}
        </label>
        {hint && (
          <BaseItem
            variant={showError ? 'error' : 'hint'}
            disabled={disabledOrAriaDisabled}
            {...hintProps}
          >
            {hint}
          </BaseItem>
        )}
      </Flex>
    );
  },
);
(Checkbox as typeof Checkbox & { displayName: string }).displayName =
  COMPONENT_NAME;

type CheckboxIconProps = {
  state: boolean | 'indeterminate';
};

/** Determines what svg to display based on the checked and indeterminate attributes. */
const CheckboxIconSvg = memo((props: CheckboxIconProps) => {
  switch (props.state) {
    case true:
      return <CheckmarkSmallIcon />;
    case 'indeterminate':
      return <MinusIcon />;
    case false:
    default:
      return null;
  }
});
